import { isEqual } from 'lodash'
import queryString from 'query-string'

// ---------- Field contracts ----------
export type QueryValue = string | string[] | undefined

export type FieldCodec<UI, API> = {
  // UI default
  default: UI

  // URL <-> UI
  fromQuery?: (raw: QueryValue) => UI
  toQuery?: (val: UI, ctx: { defaults: UI }) => QueryValue

  // UI -> API
  toApi?: (val: UI, ctx: { allUI: any }) => API | undefined

  // Optional deep equality for dirty checks (fallback to lodash isEqual)
  equals?: (a: UI, b: UI) => boolean
}

export type FilterSchema<UIShape, APIShape> = {
  // Map UI keys -> field codecs
  fields: { [K in keyof UIShape]-?: FieldCodec<UIShape[K], any> }

  // Optional post-process UI after field-level fromQuery/defaults
  finalizeUI?: (
    ui: Partial<UIShape>,
    ctx: {
      defaults: UIShape
      rawQuery: queryString.ParsedQuery<string>
    }
  ) => Partial<UIShape>

  // Optional post-process for API after fields map
  finalizeApi?: (api: Partial<APIShape>, ui: Partial<UIShape>) => APIShape

  // Optional post-process for query string
  finalizeQuery?: (flat: Record<string, QueryValue>, ui: Partial<UIShape>) => Record<string, QueryValue>
}

function makeEqualityMap<UI>(
  fields: { [K in keyof UI]-?: FieldCodec<UI[K], any> }
) {
  type EqMap = { [K in keyof UI]: (a: UI[K], b: UI[K]) => boolean }
  const m: Partial<EqMap> = {}

  // Narrow to string keys for Object.keys while keeping type info
  for (const k of Object.keys(fields) as Array<Extract<keyof UI, string>>) {
    const f = fields[k] as FieldCodec<UI[typeof k], any>
    ;(m as any)[k] = f.equals ?? ((a: any, b: any) => isEqual(a, b))
  }

  return m as EqMap
}

export function buildUseFilterOptions<UI extends Record<string, any>, API extends Record<string, any>>(
  schema: FilterSchema<UI, API>,
) {
  const defaults = Object.fromEntries(
    Object.entries(schema.fields).map(([k, f]) => [k, (f as any).default]),
  ) as UI

  const equalsMap = makeEqualityMap<UI>(schema.fields)

  function normalizeQueryParams(qp: queryString.ParsedQuery<string>): Partial<UI> {
    const out: Partial<UI> = {}
    for (const [key, field] of Object.entries(schema.fields) as [keyof UI, FieldCodec<any, any>][]) {
      const raw = qp[key as string] as QueryValue
      out[key] = field.fromQuery ? field.fromQuery(raw) : (raw as any ?? field.default)
    }
    return schema.finalizeUI ? schema.finalizeUI(out, { defaults, rawQuery: qp }) : out
  }

  function apiQueryParams(ui: Partial<UI>): API {
    const api: Partial<API> = {}
    for (const [key, field] of Object.entries(schema.fields) as [keyof UI, FieldCodec<any, any>][]) {
      const v = (ui[key] ?? field.default) as any
      if (field.toApi) {
        const mapped = field.toApi(v, { allUI: ui })
        if (mapped !== undefined) (api as any)[key] = mapped
      }
    }
    return schema.finalizeApi ? schema.finalizeApi(api, ui) : (api as API)
  }

  function stringifyFilters(ui: Partial<UI>): string {
    const flat: Record<string, QueryValue> = {}
    for (const [key, field] of Object.entries(schema.fields) as [keyof UI, FieldCodec<any, any>][]) {
      const v = (ui[key] ?? field.default) as any
      const q = field.toQuery ? field.toQuery(v, { defaults: field.default }) : (v as any)
      flat[key as string] = q
    }
    const finalized = schema.finalizeQuery ? schema.finalizeQuery(flat, ui) : flat
    // common defaults: drop empties
    const cleaned = Object.fromEntries(
      Object.entries(finalized).filter(([, v]) => {
        if (v === undefined || v === '') return false
        if (Array.isArray(v) && v.length === 0) return false
        return true
      }),
    )
    return queryString.stringify(cleaned)
  }

  function diff(defaultsObj: UI, current: Partial<UI>) {
    const keys = new Set<keyof UI & string>([
      ...Object.keys(defaultsObj),
      ...Object.keys(current as any),
    ])
    const changed: (keyof UI)[] = []
    for (const k of keys) {
      const eq = equalsMap[k]
      const a = (defaultsObj as any)[k]
      const b = (current as any)[k]
      if (!eq(a, b)) changed.push(k as keyof UI)
    }
    return { changedKeys: changed, count: changed.length }
  }

  return {
    defaultFilters: defaults,
    normalizeQueryParams,
    apiQueryParams,
    stringifyFilters,
    equalsMap,
    diff,
  }
}
